import axios from 'axios';
import toast from 'react-hot-toast';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance with timeout
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout to prevent hanging requests
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error
      const { status, data } = error.response;
      
      if (status === 401) {
        const isOTPEndpoint = error.config?.url?.includes('verify-otp') || 
                            error.config?.url?.includes('resend-otp');
        if (isOTPEndpoint) {
          // Don't redirect for OTP endpoints
          logger.api('OTP 401 error:', error.response.data);
        } else {
          // Redirect for other endpoints
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      } else if (status === 403) {
        toast.error('You do not have permission to perform this action.');
      } else if (status === 404) {
        // Check if this is a messaging-related 404 (expected for new conversations)
        const url = error.config?.url || '';
        const isMessagingEndpoint = url.includes('/messages') || 
                                     url.includes('/conversations') ||
                                     url.includes('/unread-count');
        // Check if this is a bodyguard request endpoint (may 404 after acceptance)
        const isBodyguardRequestEndpoint = url.includes('/bodyguard/requests');
        // Only show toast for non-messaging and non-bodyguard-request 404s
        if (!isMessagingEndpoint && !isBodyguardRequestEndpoint) {
          toast.error('Requested resource not found.');
        }
      } else if (status === 500) {
        toast.error('Server error. Please try again later.');
      } else {
        // For other errors, check if it's a messaging endpoint
        const url = error.config?.url || '';
        const isMessagingEndpoint = url.includes('/messages') || 
                                     url.includes('/conversations') ||
                                     url.includes('/unread-count');
        // Only show error toast for non-messaging endpoints
        if (!isMessagingEndpoint) {
          toast.error(data.message || 'An error occurred.');
        }
      }
    } else if (error.request) {
      // Request made but no response
      toast.error('Network error. Please check your connection.');
    } else {
      // Something else happened
      toast.error('An unexpected error occurred.');
    }
    
    return Promise.reject(error);
  }
);

const authAPI = {
  // Fix all methods to use 'api' not 'axios'
  login: (credentials) => 
    api.post('/auth/login', credentials),  // 'api' not 'axios'
  
  register: (userData) => 
    api.post('/auth/register', userData),
  
  getProfile: () =>
    api.get('/auth/profile'),
  
  updateProfile: (data) => 
    api.put('/auth/profile', data),
  
  changePassword: (passwordData) => 
    api.put('/auth/change-password', passwordData),
  
  logout: () =>
    api.post('/auth/logout'),
  
  // 2FA endpoints - MUST use 'api' instance
  verifyOTP: (otp, partialToken, trustDevice = false) => {
    logger.api('Calling verify-otp with:', { otp, hasPartialToken: !!partialToken, trustDevice });
    return api.post('/auth/verify-otp', { 
      otp, 
      partialToken,
      trustDevice 
    });
  },
  
  resendOTP: (partialToken) => {
    logger.api('Calling resend-otp with partial token:', !!partialToken);
    return api.post('/auth/resend-otp', { 
      partialToken 
    });
  },
  
  toggle2FA: (enable, password) => {
    logger.api('Calling toggle 2FA:', { enable });
    return api.post('/auth/2fa/toggle', { 
      enable, 
      password 
    });
  },
  
  // Test endpoint
  testOTP: (email) =>
    api.post('/auth/test-otp', { email })
};

const customerAPI = {
  // Bodyguard methods
  getBodyguards: (params) => api.get('/customer/bodyguards', { params }),
  getBodyguardDetails: (id) => api.get(`/customer/bodyguards/${id}`),
  saveBodyguard: (id) => api.post(`/customer/bodyguards/${id}/save`),
  unsaveBodyguard: (id) => api.delete(`/customer/bodyguards/${id}/save`),
  
  // Session methods
  createSession: (data) => api.post('/customer/sessions', data), // Old flow - to be deprecated
  createCheckout: (data) => api.post('/payments/checkout', data), // New flow - payment first
  getCreditBalance: () => api.get('/customer/credits'),
  getSessions: (params = {}) => api.get('/customer/sessions', { params: { all: true, ...params } }),
  getSessionDetails: (id) => api.get(`/customer/sessions/${id}`),
  getCancellationPreview: (id) => api.get(`/customer/sessions/${id}/cancellation-preview`),
  cancelSession: (id, reason, cancellationType = 'refund') => api.post(`/customer/sessions/${id}/cancel`, { reason, cancellationType }),
  rateSession: (id, data) => api.post(`/customer/sessions/${id}/rate`, data),
  downloadReceipt: (id) => api.get(`/customer/sessions/${id}/receipt`, { responseType: 'blob' }),
  
  // Payment methods
  getPaymentMethods: () => api.get('/customer/payment-methods'),
  addPaymentMethod: (data) => api.post('/customer/payment-methods', data),
  deletePaymentMethod: (id) => api.delete(`/customer/payment-methods/${id}`),
  setDefaultPaymentMethod: (id) => api.put(`/customer/payment-methods/${id}/default`),
  
  // Profile methods
  updateProfile: (data) => api.put('/customer/profile', data),
  updateNotificationPreferences: (data) => api.put('/customer/notifications', data),
  
  // Emergency contacts
  getEmergencyContacts: () => api.get('/customer/emergency-contacts'),
  addEmergencyContact: (data) => api.post('/customer/emergency-contacts', data),
  updateEmergencyContact: (id, data) => api.put(`/customer/emergency-contacts/${id}`, data),
  deleteEmergencyContact: (id) => api.delete(`/customer/emergency-contacts/${id}`),
  
  // Stats and history
  getStats: () => api.get('/customer/stats'),
  getLoyaltyPoints: () => api.get('/customer/loyalty-points'),
  
  // Cancellation handling
  getReplacementOptions: (sessionId) => api.get(`/customer/sessions/${sessionId}/replacements`),
  handleBodyguardCancellation: (sessionId, data) => api.post(`/customer/sessions/${sessionId}/handle-cancellation`, data),
};

const bodyguardAPI = {
  submitApplication: (data) => api.post('/bodyguard/application', data),
  getApplicationStatus: () => api.get('/bodyguard/application/status'),
  updateAvailability: (data) => api.put('/bodyguard/availability', data),
  getRequests: (params) => api.get('/bodyguard/requests', { params }),
  acceptRequest: (id) => api.post(`/bodyguard/requests/${id}/accept`),
  rejectRequest: (id, reason) => api.post(`/bodyguard/requests/${id}/reject`, { reason }),
  getActiveSessions: () => api.get('/bodyguard/sessions/active'),
  completeSession: (id, data = {}) => api.post(`/bodyguard/sessions/${id}/complete`, data),
  cancelSession: (id, data) => api.post(`/bodyguard/sessions/${id}/cancel`, data),
  getReplacementBodyguards: (id) => api.get(`/bodyguard/sessions/${id}/replacements`),
  getEarnings: (params) => api.get('/bodyguard/earnings', { params }),
  updateBankDetails: (data) => api.put('/bodyguard/bank-details', data),
  getSessionDetails: (id) => api.get(`/bodyguard/sessions/${id}`),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/bodyguard/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  updateLocation: (locationData) => api.put('/bodyguard/location', locationData),
};

const adminAPI = {
  getDashboardStats: () => api.get('/admin/dashboard/stats'),
  getApplications: (params) => api.get('/admin/applications', { params }),
  getApplicationDetails: (id) => api.get(`/admin/applications/${id}`),
  reviewApplication: (id, data) => api.post(`/admin/applications/${id}/review`, data),
  getUsers: (params) => api.get('/admin/users', { params }),
  getUserDetails: (id) => api.get(`/admin/users/${id}`),
  updateUserStatus: (id, status) => api.put(`/admin/users/${id}/status`, { status }),
  getSessions: (params) => api.get('/admin/sessions', { params }),
  getSessionDetails: (id) => api.get(`/admin/sessions/${id}`),
  resolveIncident: (sessionId, incidentId, resolution) =>
    api.post(`/admin/sessions/${sessionId}/incidents/${incidentId}/resolve`, { resolution }),
  getAnalytics: (params) => api.get('/admin/analytics', { params }),
  // Waitlist management
  getWaitlist: (params) => api.get('/waitlist', { params }),
  getWaitlistStats: () => api.get('/waitlist/stats'),
  updateWaitlistEntry: (id, data) => api.patch(`/waitlist/${id}`, data),
  deleteWaitlistEntry: (id) => api.delete(`/waitlist/${id}`),
};

const sessionAPI = {
  verifySession: (id, code) => api.post(`/session/${id}/verify`, { code }),
  getActiveSession: () => api.get('/session/active'),
  reportIncident: (id, data) => api.post(`/session/${id}/incident`, data),
  addNote: (id, note) => api.post(`/session/${id}/note`, { note }),
};

const notificationAPI = {
  getNotifications: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: (category) => api.put('/notifications/mark-all-read', { category }),
  deleteNotification: (id) => api.delete(`/notifications/${id}`),
  createTestNotification: () => api.post('/notifications/test'),
};

const uploadFile = async (file, type = 'document') => {
  const formData = new FormData();
  formData.append('file', file);

  // Pass type in the URL instead of body
  const response = await api.post(`/upload/${type}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

// Public bodyguard onboarding (no auth). Submits the application JSON plus all
// document files in a single multipart request to the onboarding backend.
const applicationAPI = {
  submit: (formData) =>
    api.post('/applications', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

export {
  api,
  authAPI,
  customerAPI,
  bodyguardAPI,
  adminAPI,
  sessionAPI,
  notificationAPI,
  uploadFile,
  applicationAPI,
};